import React, { useEffect, useState } from "react";
import Pusher from "pusher-js";
import { useRouter } from "next/router";
import {
  getLatestBid_AuctionFloor,
  getAllBidsForBanker_AuctionFloor,
  getAllWrongBidsForBanker_AuctionFloor,
  getBidderSessionData_AuctionFloor,
} from "services";
import { DateTime } from "luxon";
import { BankerEventDataV2 } from "app-v2";
import AuctionLoader from "../bidder/Tabs/auctionFloor/AuctionLoader";
import FloatingOnlineUsers from "./FloatingOnlineUsers";
import { useSelector } from "react-redux";

interface BankerAuctionFloorV2Props {
  data: {
    uuid: string;
    status: string;
    start_date_time: string;
    reserve_price: string;
    final_end_date: string;
  };
}

function BankerAuctionFloorV2(props: BankerAuctionFloorV2Props) {
  const [amount, setAmount] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [status, setStatus] = useState("");
  const [currentTime, setCurrentTime] = useState<any>(0);
  const [bidsEvent, setBidsEvent] = useState<any[]>([]);
  const [latestBid, setLatestBid] = useState<any>(null);
  const [prevBid, setPrevBid] = useState<any>(null);
  const [wrongBidsEvent, setWrongBidsEvent] = useState<any[]>([]);
  const [loading, setLoading] = useState<any>(true);
  const [onlineMembers, setOnlineMembers] = useState<any[]>([]);
  const [bidderSessionData, setBidderSessionData] = useState<any>(null);
  const [isOnlineEventTriggered, setIsOnlineEventTriggered] =
    useState<any>(false);

  const {
    query: { id },
  } = useRouter();

  const router = useRouter();
  const accessToken = useSelector((state: any) => state.bankerAuth.accessToken);
  const bankerName = useSelector(
    (state: any) => state.bankerAuth.user?.profile?.bio?.full_name,
  );

  // pusher functionality
  useEffect(() => {
    Pusher.logToConsole = false;
    let authConfig = {};
    let channelId = `auction-event-${id}`;

    if (process.env.NEXT_PUBLIC_ENABLE_BIDDER_ACTIVITY === "true") {
      authConfig = {
        authEndpoint: `${process.env.NEXT_PUBLIC_API_URL}/auction-floor/pusher/auth`,
        auth: {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            event_uuid: id,
            user_type: "banker",
            user_name: bankerName,
          },
        },
      };
      channelId = `presence-${channelId}`;
    }

    const pusher = new Pusher(`${process.env.NEXT_PUBLIC_PUSHER_KEY}`, {
      cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER,
      ...authConfig,
    });

    const channel = pusher.subscribe(channelId);

    channel.bind("bidding_value_has_been_changed", (data: any) => {
      handleDataChange(data);
      setBidsEvent((prev) => [...prev, data]);
    });

    channel.bind("user_placed_wrong_bid", (data: any) => {
      setWrongBidsEvent((prev) => [...prev, data]);
    });

    channel.bind("auction_event_has_started", (data: any) => {
      if (data) {
        setStatus("live");
      }
    });

    channel.bind("auction_event_has_ended", (data: any) => {
      if (data) {
        setStatus("end");
      }
    });

    pusher.connection.bind("error", function (err: any) {
      if (err?.error?.data?.code === 4004) {
        console.log(">>> detected limit error");
      } else if (err?.data?.code === 1006) {
        // Websocket: CLOSED ABNORMALLY
      }
    });

    pusher.connection.bind("disconnected", function (err: any) {
      console.log(">>> disconnected");
      // setConnectionStatus("Offline");
    });

    pusher.connection.bind("connected", function (err: any) {
      console.log("connected", pusher.sessionID);
      // setConnectionStatus("Online");
    });

    // pusher.connection.bind("state_change", (states: any) => {
    //   const { current, previous } = states;
    //   // console.log(`current state: ${current}, previous state: ${previous}`);
    //   switch (current) {
    //     case "failed":
    //       setConnectionStatus("Offline");
    //       break;
    //     case "disconnected":
    //       setConnectionStatus("Offline");
    //       break;
    //     case "unavailable":
    //       setConnectionStatus("Offline");
    //       break;
    //     case "connected":
    //       setConnectionStatus("Online");
    //       break;
    //     case "connecting":
    //       setConnectionStatus("Offline");
    //       break;
    //     default:
    //       break;
    //   }
    // });

    //----------Pusher members---------
    if (process.env.NEXT_PUBLIC_ENABLE_BIDDER_ACTIVITY === "true") {
      // Set up presence events
      channel.bind("pusher:subscription_succeeded", (members: any) => {
        const memberList = Object.entries(members.members).map(
          ([id, member]: [string, any]) => ({
            id: id,
            name: member.name,
            user_type: member.user_type,
          }),
        );
        setOnlineMembers(memberList);
      });

      channel.bind("pusher:member_added", (member: any) => {
        setOnlineMembers((prev: any) => {
          // Check if member already exists to prevent duplicates
          const exists = prev.some((m: any) => m.id === member.id);
          if (exists) {
            return prev;
          }
          return [
            ...prev,
            {
              id: member.id,
              name: member.info.name,
              user_type: member.info.user_type,
            },
          ];
        });
      });

      channel.bind("pusher:member_removed", (member: any) => {
        setOnlineMembers((prev: any) =>
          prev.filter((m: any) => m.id !== member.id),
        );
      });
    }
    return function cleanup() {
      pusher.disconnect();
    };
  }, [id]);

  useEffect(() => {
    if (props.data) {
      (async () => {
        const allBid: any = await getAllBidsForBanker_AuctionFloor(id);

        const allWrongBids: any = await getAllWrongBidsForBanker_AuctionFloor(
          id,
        );

        if (allBid) {
          setBidsEvent(allBid);
          if (allBid.length >= 2) {
            setPrevBid(allBid[allBid.length - 2]);
          }
        }
        if (allWrongBids) {
          setWrongBidsEvent(allWrongBids);
        }
        const lastBid: any = await getLatestBid_AuctionFloor(id);
        setLatestBid(lastBid);
        setStatus(props.data?.status);
        const start_timestamp = DateTime.fromISO(props.data?.start_date_time)
          .setZone("Asia/Kolkata")
          .toMillis();
        setStartTime(start_timestamp);

        if (lastBid) {
          setAmount(lastBid.final_amount);
          setEndTime(parseInt(lastBid.final_end_time));
        } else {
          setAmount(parseInt(props.data?.reserve_price));
          setEndTime(parseInt(props.data?.final_end_date));
        }
        setLoading(false);
      })();
    }
  }, [props.data]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(
        DateTime.now().setZone("Asia/Kolkata").toFormat("hh:mm:ss a"),
      );
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (isOnlineEventTriggered) {
      router.reload();
    }
  }, [isOnlineEventTriggered]);

  const handleDataChange = async (data: any) => {
    setAmount(data?.final_amount);
    setEndTime(data?.final_end_time);
    if (latestBid) {
      setPrevBid(latestBid);
    }

    const lastBid: any = await getLatestBid_AuctionFloor(id);

    setLatestBid((prev: any) => {
      setPrevBid(prev);
      return lastBid;
    });
  };

  const getBidderSessionDataForBanker = async (id: any) => {
    try {
      const data = await getBidderSessionData_AuctionFloor(id);
      setBidderSessionData(data);
      return data;
    } catch (error) {
      console.error("Error fetching session data:", error);
      return null;
    }
  };

  useEffect(() => {
    (async () => {
      if (process.env.NEXT_PUBLIC_ENABLE_BIDDER_ACTIVITY === "true" && id) {
        await getBidderSessionDataForBanker(id);
      }
    })();
  }, [id, status]);

  // console.log("onlineMembers", onlineMembers);
  return (
    <div className="rounded-lg">
      <div className="mb-8">
        {loading ? (
          <AuctionLoader />
        ) : (
          <>
            <BankerEventDataV2
              data={props.data}
              latestBidAmount={amount}
              startTime={startTime}
              endTime={endTime}
              status={status}
              currentTime={currentTime}
              latestBid={latestBid}
              prevBid={prevBid}
              correctBids={bidsEvent}
              wrongBids={wrongBidsEvent}
              setStatus={setStatus}
            />
            {process.env.NEXT_PUBLIC_ENABLE_BIDDER_ACTIVITY === "true" && (
              // {/* Floating Online Users */}
              <FloatingOnlineUsers
                onlineMembers={onlineMembers}
                status={status}
                bidderSessionData={bidderSessionData}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default BankerAuctionFloorV2;
